/* ===========================================================
   ui/proto.jsx — Proto, the mascot (on loan from Weather Protocol)

   A little worker robot: hard hat with a radar dish, a screen for a
   face, a hi-vis vest. In Docs Protocol he carries a pencil. He gives
   the welcome tour and turns up with a line whenever something goes
   wrong (errors, a missing page, no internet), so a bad moment still
   feels like the app. Holding a turning wrench = the problem is ours.

   translate(err) turns codes ("HTTP 503", "Failed to fetch") into
   plain words. Raw codes only ever appear as faint small print.
   =========================================================== */

export function Proto({ size = 72, fixing = false, pose = 'pencil', class: cls = '' }) {
  const arm = fixing ? 'wrench' : pose;
  return (
    <div class={`proto ${fixing ? 'fixing' : ''} ${cls}`} style={{ width: `${size}px`, height: `${size}px` }} aria-hidden="true">
      <svg viewBox="0 0 100 100" width={size} height={size}>
        <g class="proto-body">
          <line x1="50" y1="14" x2="50" y2="6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
          <path d="M42 7 Q50 1 58 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
          <circle class="proto-ping" cx="50" cy="5" r="2.4" fill="currentColor" />
          <path d="M24 34 Q24 14 50 14 Q76 14 76 34 Z" fill="#ffc83d" />
          <rect x="19" y="32" width="62" height="6" rx="3" fill="#e9a91c" />
          <rect x="47" y="15" width="6" height="18" rx="2" fill="#f1b72a" />
          <rect x="26" y="38" width="48" height="34" rx="11" fill="#0d1a14" stroke="currentColor" stroke-width="2.5" />
          <g class="proto-eyes" fill="currentColor">
            <rect x="37" y="48" width="7" height="9" rx="3.5" />
            <rect x="56" y="48" width="7" height="9" rx="3.5" />
          </g>
          <rect class="proto-mouth" x="44" y="62" width="12" height="3" rx="1.5" fill="currentColor" />
          <path d="M32 74 L68 74 L72 96 L28 96 Z" fill="#ff7a2f" />
          <path d="M40 74 L46 96 M60 74 L54 96" stroke="#e8ff7a" stroke-width="3" />
          <rect x="45" y="74" width="10" height="22" fill="#0d1a14" opacity=".35" />
          {arm === 'pencil' && (
            <g class="proto-arm pencil">
              <path d="M70 78 Q82 74 85 62" fill="none" stroke="#ff7a2f" stroke-width="6" stroke-linecap="round" />
              <g transform="rotate(28 86 52)">
                <rect x="83" y="36" width="6" height="20" rx="1" fill="#ffd54a" />
                <rect x="83" y="33" width="6" height="4" rx="1" fill="#ff9aa8" />
                <path d="M83 56 L89 56 L86 63 Z" fill="#f2d2a9" />
                <path d="M85 60.5 L87 60.5 L86 63 Z" fill="#333" />
              </g>
              <circle cx="85" cy="60" r="4.5" fill="#ffc83d" />
            </g>
          )}
          {arm === 'wave' && (
            <g class="proto-arm"><path d="M70 78 Q84 70 86 58" fill="none" stroke="#ff7a2f" stroke-width="6" stroke-linecap="round" />
              <circle cx="86" cy="56" r="4.5" fill="#ffc83d" /></g>
          )}
          {arm === 'wrench' && (
            <g class="proto-wrench">
              <path d="M70 80 Q80 78 84 70" fill="none" stroke="#ff7a2f" stroke-width="6" stroke-linecap="round" />
              <g class="proto-wrench-tool">
                <rect x="82" y="46" width="5" height="24" rx="2.2" fill="#c7ced6" />
                <path d="M78.5 40 a6.5 6.5 0 1 0 12 0 l-3.2 0 l0 4.5 l-5.6 0 l0 -4.5 z" fill="#c7ced6" />
              </g>
              <circle cx="84" cy="70" r="4.5" fill="#ffc83d" />
            </g>
          )}
          <path d="M30 78 Q20 86 22 94" fill="none" stroke="#ff7a2f" stroke-width="6" stroke-linecap="round" />
        </g>
      </svg>
    </div>
  );
}

/** A whole page or box that failed: Proto + what happened + what to do. */
export function ProtoCard({ title, text, error, ours, actions, small = false }) {
  const t = error ? translate(error) : null;
  const isOurs = ours ?? t?.ours ?? false;
  return (
    <div class={`proto-card ${small ? 'small' : ''}`} role="alert">
      <Proto size={small ? 56 : 88} fixing={isOurs} />
      <div class="proto-card-body">
        {title && <h2>{title}</h2>}
        <p>{text || t?.text}</p>
        {actions && <div class="proto-card-actions">{actions}</div>}
        {t?.code && <p class="fine">{String(t.code).slice(0, 40)}</p>}
      </div>
    </div>
  );
}

const pick = a => a[Math.floor(Math.random() * a.length)];

const CODES = {
  400: ['Something about that request was a bit off. Try it again.', false],
  401: ['You need to be signed in for that.', false],
  403: ['That one isn\'t allowed for this account.', false],
  404: ['I couldn\'t find that. It may have been deleted, or it isn\'t shared with you.', false],
  408: ['That took too long. The connection might be slow.', false],
  409: ['That clashed with something that was already there. Try again.', false],
  413: ['That was too big to save.', false],
  415: ['That kind of file won\'t work here.', false],
  429: ['Too many tries in a row. Give it a minute, then try again.', false],
  500: ['Our server hit a snag. Your work is safe on this device; I\'m on it.', true],
  502: ['Our server isn\'t answering right now. Your work is safe here and will sync later.', false],
  503: ['Our server is busy for a moment. Your work is safe here and will sync later.', false],
  504: ['Our server took too long to answer. Try again in a bit.', false],
};

export function translate(what) {
  const raw = String(what?.message ?? what ?? '');
  const status = what?.status ?? Number(raw.match(/\bHTTP (\d{3})\b/)?.[1] || 0);
  // our Worker's own sentences come through as they are
  if (status && raw && !/^HTTP \d{3}$/.test(raw) && !/failed to fetch/i.test(raw)) return { text: raw, ours: status >= 500, code: `HTTP ${status}` };
  if (status && CODES[status]) return { text: CODES[status][0], ours: CODES[status][1], code: `HTTP ${status}` };
  if (status >= 500) return { text: 'Our server is having trouble. Your work is safe on this device.', ours: true, code: `HTTP ${status}` };
  if (what?.offline || /failed to fetch|networkerror|load failed|network request failed/i.test(raw) || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    return { text: 'I couldn\'t reach the internet. Keep going: everything is saved on this device and will sync when you\'re back.', ours: false, code: 'offline' };
  }
  if (/aborterror|aborted|timed? ?out|timeout/i.test(raw)) return { text: 'That took too long to answer. The connection might be slow.', ours: false, code: 'timeout' };
  if (/quotaexceeded|storage.*full/i.test(raw)) return { text: 'This device\'s storage for the app is full. Free up some space so I can keep saving here.', ours: false, code: 'storage' };
  if (/notallowederror|permission|denied/i.test(raw)) return { text: 'Your browser said no to that. You can allow it in the browser\'s site settings.', ours: false, code: 'permission' };
  if (/is not defined|cannot read|undefined is not|is not a function|is not iterable|unexpected token|syntaxerror|typeerror|referenceerror|rangeerror/i.test(raw)) {
    return { text: 'A bug in the app tripped me up. That one\'s our fault; I\'m fixing it.', ours: true, code: raw.slice(0, 120) };
  }
  return { text: raw || 'Something went wrong.', ours: false, code: '' };
}

export const OOPS = [
  'Oops. I spilled ink on the server.',
  'Well, that page jammed in the printer.',
  'Something hiccuped. Even robots get the hiccups.',
  'Uh-oh. I dropped my clipboard.',
  'Hmm. My pencil just snapped.',
];
export const LOST = [
  'This page doesn\'t exist. I checked every drawer in the filing cabinet.',
  'Nothing here. Not even a sticky note.',
  'I looked everywhere. This page must have blown off my desk.',
  'You\'ve wandered off the page. Happens to the best of us.',
];
export const OFFLINE = [
  'No internet! Keep writing: I\'m saving every word on this device.',
  'We lost the signal. Your words are safe here and will sync when it\'s back.',
  'Offline. Don\'t worry, I\'ve got your back (and your paragraphs).',
];
export const BACK_ONLINE = [
  'We\'re back online! Syncing your changes now.',
  'Signal\'s back. Sending everything up.',
];
export const oops = () => pick(OOPS);
export const lost = () => pick(LOST);
export const offlineLine = () => pick(OFFLINE);
export const backOnlineLine = () => pick(BACK_ONLINE);
